import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import Plot from 'react-plotly.js'
import { searchApi } from '../api/search'
import { useCorpus } from '../contexts/CorpusContext'

interface DbStats {
  total_articles: number
  oa_stats: Record<string, number>
  review_stats: Record<string, number>
  year_range: [number, number]
  year_counts: Array<{ year: number; count: number }>
  year_oa_counts: Record<string, Record<string, number>>
  year_oa_review_counts: Record<string, Record<string, number>>
}

export default function HomePage() {
  const { activeCorpusId, activeCorpus } = useCorpus()
  const [stats, setStats] = useState<DbStats | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    setIsLoading(true)
    searchApi.getStats(activeCorpusId || undefined)
      .then(r => setStats(r.data as DbStats))
      .catch(() => {})
      .finally(() => setIsLoading(false))
  }, [activeCorpusId])

  const years = stats
    ? Object.keys(stats.year_oa_counts || {})
        .map(Number)
        .sort((a, b) => a - b)
    : []

  const seg = (y: number, key: string) => stats!.year_oa_review_counts?.[String(y)]?.[key] || 0
  const oaResearch = years.map(y => seg(y, 'OA|Research'))
  const oaReview = years.map(y => seg(y, 'OA|Review'))
  const abstractResearch = years.map(y => seg(y, 'Abstract_only|Research'))
  const abstractReview = years.map(y => seg(y, 'Abstract_only|Review'))

  return (
    <div className="space-y-8">
      {/* Hero */}
      <div className="text-center py-8">
        <div className="flex items-center justify-center gap-3 mb-4">
          <img src="/logo.png" alt="PMC AI Search" className="h-16 w-auto" />
          <h1 className="text-4xl font-bold text-slate-800">PMC AI Search</h1>
        </div>
        <p className="text-lg text-slate-600 max-w-2xl mx-auto">
          A powerful literature mining tool for Extracellular Vesicle research. 
          Search across the full PMC Open Access corpus with regex-based keyword 
          extraction, co-occurrence filtering, and AI-powered analysis.
        </p>
        <div className="mt-6">
          <Link
            to="/search"
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            Start Searching →
          </Link>
        </div>
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-3 gap-4 max-w-3xl mx-auto">
          <div className="bg-white rounded-xl border p-5 text-center shadow-sm">
            <div className="text-3xl font-bold text-blue-600">{stats.total_articles.toLocaleString()}</div>
            <div className="text-sm text-gray-600 mt-1">Total Articles</div>
            <div className="text-xs text-gray-400 mt-1">{stats.year_range[0]} – {stats.year_range[1]}</div>
          </div>
          <div className="bg-white rounded-xl border p-5 text-center shadow-sm">
            <div className="text-3xl font-bold text-green-600">{(stats.oa_stats?.OA || 0).toLocaleString()}</div>
            <div className="text-sm text-gray-600 mt-1">Open Access</div>
            <div className="text-xs text-gray-400 mt-1">Full-text available</div>
          </div>
          <div className="bg-white rounded-xl border p-5 text-center shadow-sm">
            <div className="text-3xl font-bold text-orange-600">{(stats.oa_stats?.['Abstract_only'] || 0).toLocaleString()}</div>
            <div className="text-sm text-gray-600 mt-1">Abstract Only</div>
            <div className="text-xs text-gray-400 mt-1">and metadata</div>
          </div>
        </div>
      )}

      {/* Corpus coverage — OA vs Abstract-only, split by Research/Review */}
      <div className="bg-white rounded-xl border p-6 shadow-sm">
        <h2 className="text-xl font-semibold text-slate-800 mb-2">
          Corpus Coverage Over Time
          {activeCorpus && <span className="text-base font-normal text-gray-500"> — {activeCorpus.name}</span>}
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          Articles per year by access type (Open Access vs Abstract-only) and article type (research vs review)
          {stats && (
            <> — {(stats.review_stats?.Research || 0).toLocaleString()} research, {(stats.review_stats?.Review || 0).toLocaleString()} reviews in total.</>
          )}
        </p>
        {isLoading ? (
          <div className="text-center py-12 text-gray-400">Loading statistics…</div>
        ) : stats && years.length > 0 ? (
          <Plot
            data={[
              {
                x: years.map(String), y: oaResearch, name: 'OA · Research', type: 'bar',
                marker: { color: '#15803d' },
                hovertemplate: '%{x}: %{y} OA research<extra></extra>',
              },
              {
                x: years.map(String), y: oaReview, name: 'OA · Review', type: 'bar',
                marker: { color: '#4ade80' },
                hovertemplate: '%{x}: %{y} OA reviews<extra></extra>',
              },
              {
                x: years.map(String), y: abstractResearch, name: 'Abstract · Research', type: 'bar',
                marker: { color: '#c2410c' },
                hovertemplate: '%{x}: %{y} abstract-only research<extra></extra>',
              },
              {
                x: years.map(String), y: abstractReview, name: 'Abstract · Review', type: 'bar',
                marker: { color: '#fdba74' },
                hovertemplate: '%{x}: %{y} abstract-only reviews<extra></extra>',
              },
            ]}
            layout={{
              barmode: 'stack',
              xaxis: { title: 'Year', tickangle: -45 },
              yaxis: { title: 'Number of Articles' },
              legend: { orientation: 'h', y: 1.12 },
              margin: { t: 50, b: 60, l: 60, r: 20 },
              paper_bgcolor: 'rgba(0,0,0,0)',
              plot_bgcolor: 'rgba(0,0,0,0)',
              autosize: true,
            }}
            config={{ responsive: true, displayModeBar: false }}
            style={{ width: '100%', height: '420px' }}
          />
        ) : (
          <div className="text-center py-12 text-gray-400">No statistics available.</div>
        )}
      </div>

      {/* Feature cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl border p-6 shadow-sm">
          <div className="text-2xl mb-3">🔍</div>
          <h3 className="font-semibold text-slate-800 mb-2">Regex Keyword Search</h3>
          <p className="text-sm text-gray-600">
            Search with plain terms or regular expressions across the full or specific sections of manuscript text.
            Extract specific entities like miRNA names, proteins, or cell types automatically.
          </p>
        </div>
        <div className="bg-white rounded-xl border p-6 shadow-sm">
          <div className="text-2xl mb-3">🔗</div>
          <h3 className="font-semibold text-slate-800 mb-2">Co-occurrence Filtering</h3>
          <p className="text-sm text-gray-600">
            Filter results to articles where multiple keywords appear together in the same
            manuscript, section, or sentence.
          </p>
        </div>
        <div className="bg-white rounded-xl border p-6 shadow-sm">
          <div className="text-2xl mb-3">🤖</div>
          <h3 className="font-semibold text-slate-800 mb-2">AI-Powered Analysis</h3>
          <p className="text-sm text-gray-600">
            Generate summaries, diagrams, and structured analyses from your result sets
            using LLMs.
          </p>
        </div>
      </div>

      {/* Footer CTA */}
      <div className="text-center py-6">
        <Link
          to="/search"
          className="inline-flex items-center gap-2 px-6 py-3 bg-slate-800 text-white rounded-lg font-semibold hover:bg-slate-900 transition-colors"
        >
          Go to Search →
        </Link>
      </div>
    </div>
  )
}
